<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { usePlanningApi } from '../composables/usePlanningApi'
import type { ProductionOrder } from '../types/planning'

/** Production-order release on real orders: list, readiness, release, cancel. */

const { t } = useI18n()
const auth = useAuthStore()
const { listProductionOrders, updateProductionOrder } = usePlanningApi()

const PER_PAGE = 20
const statuses = ['', 'planned', 'in_progress', 'completed', 'cancelled']
const today = new Date().toISOString().slice(0, 10)

const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('production_orders.manage'))

const orders = ref<ProductionOrder[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const busyId = ref<number | null>(null)
const page = ref(1)
const total = ref(0)
const totalPages = ref(1)
const filters = ref({ search: '', status: 'planned' })

const rangeLabel = computed(() => {
  if (!total.value) return ''
  const from = (page.value - 1) * PER_PAGE + 1
  const to = Math.min(page.value * PER_PAGE, total.value)
  return t('planning.common.showing', { from, to, total: total.value })
})

/** Release blockers the UI can actually verify. */
function releaseBlockers(order: ProductionOrder): string[] {
  const blockers: string[] = []
  if (!order.start_date || !order.end_date) blockers.push(t('planning.orders.blockerDates'))
  if (Number(order.planned_quantity ?? 0) <= 0) blockers.push(t('planning.orders.blockerQuantity'))
  if (order.status !== 'planned') blockers.push(t('planning.orders.blockerStatus'))
  return blockers
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listProductionOrders({
      search: filters.value.search.trim() || undefined,
      status: filters.value.status || undefined,
      page: page.value,
      per_page: PER_PAGE,
    })
    orders.value = result.items
    total.value = result.meta.total
    totalPages.value = Math.max(1, result.meta.total_pages)
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function setStatus(order: ProductionOrder, status: 'in_progress' | 'cancelled') {
  actionError.value = ''
  actionMessage.value = ''
  if (status === 'in_progress' && !window.confirm(t('planning.orders.releaseConfirm', { code: order.mo_code }))) {
    return
  }
  busyId.value = order.id
  try {
    await updateProductionOrder(order.id, { status })
    actionMessage.value =
      status === 'in_progress' ? t('planning.orders.releasedMessage') : t('planning.orders.cancelledMessage')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = null
  }
}

function goToPage(next: number) {
  page.value = Math.min(Math.max(1, next), totalPages.value)
  void load()
}

function applyFilters() {
  page.value = 1
  void load()
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('planning.eyebrow') }}</p>
      <h2>{{ t('planning.orders.title') }}</h2>
      <p>{{ t('planning.orders.description') }}</p>
    </div>
  </div>

  <div class="toolbar">
    <input
      v-model="filters.search"
      class="search-input"
      type="search"
      :placeholder="t('planning.common.searchPlaceholder')"
    >
    <select v-model="filters.status" class="filter-select">
      <option v-for="status in statuses" :key="status || 'all'" :value="status">
        {{ status ? t(`planning.orderStatus.${status}`) : t('planning.common.allStatuses') }}
      </option>
    </select>
    <button class="btn-secondary" :disabled="loading" @click="applyFilters">
      {{ t('planning.common.apply') }}
    </button>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('planning.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('planning.common.retry') }}</button>
  </div>

  <div v-else-if="orders.length === 0" class="state-message">
    <p>{{ t('planning.orders.noOrders') }}</p>
  </div>

  <template v-else>
    <section class="table-shell">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('planning.common.order') }}</th>
            <th>{{ t('planning.common.product') }}</th>
            <th>{{ t('planning.common.department') }}</th>
            <th class="cell-num">{{ t('planning.common.planned') }}</th>
            <th class="cell-num">{{ t('planning.common.produced') }}</th>
            <th>{{ t('planning.common.startDate') }}</th>
            <th>{{ t('planning.common.endDate') }}</th>
            <th>{{ t('planning.common.status') }}</th>
            <th>{{ t('planning.orders.readiness') }}</th>
            <th class="cell-actions">{{ t('planning.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="order in orders" :key="order.id">
            <td class="cell-code">{{ order.mo_code }}</td>
            <td class="cell-name">{{ order.product_name || '—' }}</td>
            <td>{{ order.department_name || '—' }}</td>
            <td class="cell-num">{{ Number(order.planned_quantity).toLocaleString() }}</td>
            <td class="cell-num">{{ Number(order.produced_quantity).toLocaleString() }}</td>
            <td class="cell-date">{{ formatDisplayDate(order.start_date) || '—' }}</td>
            <td
              class="cell-date"
              :class="order.end_date && order.end_date < today && order.status !== 'completed' ? 'is-negative' : ''"
            >
              {{ formatDisplayDate(order.end_date) || '—' }}
            </td>
            <td>
              <span
                class="status-badge"
                :class="order.status === 'completed' ? 'status-ok' : order.status === 'cancelled' ? 'status-critical' : order.status === 'in_progress' ? 'status-info' : 'status-neutral'"
              >
                {{ t(`planning.orderStatus.${order.status}`) }}
              </span>
            </td>
            <td>
              <ul v-if="releaseBlockers(order).length" class="blocker-list">
                <li v-for="blocker in releaseBlockers(order)" :key="blocker">{{ blocker }}</li>
              </ul>
              <span v-else class="status-badge status-ok">{{ t('planning.orders.ready') }}</span>
            </td>
            <td class="cell-actions">
              <template v-if="canManage">
                <button
                  v-if="order.status === 'planned'"
                  class="link-action"
                  :disabled="busyId === order.id"
                  @click="setStatus(order, 'in_progress')"
                >
                  {{ t('planning.orders.release') }}
                </button>
                <button
                  v-if="order.status === 'planned' || order.status === 'in_progress'"
                  class="link-action danger"
                  :disabled="busyId === order.id"
                  @click="setStatus(order, 'cancelled')"
                >
                  {{ t('planning.orders.cancelOrder') }}
                </button>
              </template>
              <span v-else class="empty-inline">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <div class="pagination">
      <span>{{ rangeLabel }}</span>
      <button class="btn-secondary" :disabled="page <= 1 || loading" @click="goToPage(page - 1)">
        {{ t('planning.common.previous') }}
      </button>
      <span>{{ t('planning.common.page', { page, total: totalPages }) }}</span>
      <button class="btn-secondary" :disabled="page >= totalPages || loading" @click="goToPage(page + 1)">
        {{ t('planning.common.next') }}
      </button>
    </div>

    <p class="empty-inline">{{ t('planning.orders.releaseHint') }}</p>
  </template>
</template>

<style scoped>
.blocker-list {
  margin: 0;
  padding-left: 16px;
  color: #7a4f00;
  font-size: 12px;
}
</style>
